import React, { memo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Typography,
  IconButton,
  Button,
  Icon,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Card
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { AVALIACAO_RESPOSTA_TIPO } from '../../../enums';
import services from '../../../services';
import Range from '../_components/Range';
import QuestaoRating from '../_components/QuestaoRating';
import {
  createOpcoesDicotomicas,
  mapOpcoesDicotomicasToRespostaDominio
} from '../_shared/dicotomicaOptions';

const MAX_DISCRIMINACOES = 13;

/**
 * Retorna o rótulo ordinal da opção/faixa (ex.: 1ª, 2ª, 3ª).
 */
const getOpcaoPorIndice = pIndice => `${pIndice}ª` || '';

const OPCOES_DISCRIMINACAO = Array(MAX_DISCRIMINACOES)
  .fill()
  .map((_, xIndex) => getOpcaoPorIndice(xIndex + 1));

const createMultiplaEscolhaItem = pIndex => ({
  id: Date.now() + pIndex,
  opcao: getOpcaoPorIndice(pIndex + 1),
  pergunta: '',
  discriminacao: '',
  points: -3,
  rating: 1
});

/**
 * Cria uma faixa numérica padrão usada no modo de resposta numérica.
 */
const createFaixaNumerica = pIndex => ({
  id: Date.now() + pIndex,
  faixa: `${pIndex + 1}ª`,
  min: '',
  max: '',
  discriminacao: '',
  points: -3,
  rating: 1
});

const AvaliacoesSettingsQuestoesAdd = memo(props => {
  const navigate = useNavigate();

  const [sessoesQuestoes, setSessoesQuestoes] = useState([]);

  const { actions } = useAvaliacoesContext();

  const formik = useFormik({
    initialValues: {
      questao: '',
      tooltip: '',
      sessaoId: '',
      tipoResposta: AVALIACAO_RESPOSTA_TIPO.MUL.id,
      interna: false,
      opcoesMultiplaEscolha: [createMultiplaEscolhaItem(0)],
      opcoesDicotomicas: createOpcoesDicotomicas(),
      faixasNumericas: [createFaixaNumerica(0)]
    },
    validateOnMount: false,
    onSubmit: pValues => {
      const isQuestaoInterna = pValues.interna;

      // Validações mínimas antes de montar o payload.
      if (!pValues.questao.trim()) {
        props.onMessageError?.('Informe o título da questão.');
        return;
      }

      if (!pValues.sessaoId) {
        props.onMessageError?.('Selecione a sessão da questão.');
        return;
      }

      if (
        !isQuestaoInterna &&
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id &&
        pValues.opcoesMultiplaEscolha.some(xOpcao => !xOpcao.pergunta?.trim())
      ) {
        props.onMessageError?.('Preencha a pergunta de todas as opções de múltipla escolha.');
        return;
      }

      if (
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id &&
        pValues.opcoesMultiplaEscolha.length === 0
      ) {
        props.onMessageError?.('Adicione ao menos uma opção de múltipla escolha.');
        return;
      }

      if (pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id) {
        const xOpcaoSemPontos = pValues.opcoesMultiplaEscolha.find(
          xOpcao => xOpcao.points === null
        );
        if (xOpcaoSemPontos) {
          props.onMessageError?.('Selecione a pontuação de todas as opções.');
          return;
        }
      }

      if (!isQuestaoInterna && pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id) {
        const xOpcaoSemPontos = pValues.opcoesDicotomicas.find(xOpcao => xOpcao.points === null);
        if (xOpcaoSemPontos) {
          props.onMessageError?.('Selecione a pontuação das duas opções dicotômicas.');
          return;
        }
      }

      if (
        !isQuestaoInterna &&
        pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id &&
        pValues.faixasNumericas.length === 0
      ) {
        props.onMessageError?.('Adicione ao menos uma faixa numérica.');
        return;
      }

      if (!isQuestaoInterna && pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id) {
        const xFaixas = pValues.faixasNumericas;
        const xLastIndex = xFaixas.length - 1;

        const xFaixaSemPontos = xFaixas.find(xFaixa => xFaixa.points === null);
        if (xFaixaSemPontos) {
          props.onMessageError?.('Selecione a pontuação de todas as faixas.');
          return;
        }

        for (let xIndex = 0; xIndex < xFaixas.length; xIndex += 1) {
          const xFaixa = xFaixas[xIndex];
          const xMinPodeSerNulo = xIndex === 0;
          const xMaxPodeSerNulo = xIndex === xLastIndex;
          const xMinVazio = xFaixa.min === '' || xFaixa.min === null || xFaixa.min === undefined;
          const xMaxVazio = xFaixa.max === '' || xFaixa.max === null || xFaixa.max === undefined;

          if (!xMinPodeSerNulo && xMinVazio) {
            props.onMessageError?.(`Informe o mínimo da faixa ${xIndex + 1}.`);
            return;
          }

          if (!xMaxPodeSerNulo && xMaxVazio) {
            props.onMessageError?.(`Informe o máximo da faixa ${xIndex + 1}.`);
            return;
          }

          if (!xMinVazio) {
            const xMin = Number(xFaixa.min);
            if (!Number.isFinite(xMin) || xMin < 0) {
              props.onMessageError?.(
                `O mínimo da faixa ${xIndex + 1} deve ser numérico e não-negativo.`
              );
              return;
            }
          }

          if (!xMaxVazio) {
            const xMax = Number(xFaixa.max);
            if (!Number.isFinite(xMax) || xMax < 0) {
              props.onMessageError?.(
                `O máximo da faixa ${xIndex + 1} deve ser numérico e não-negativo.`
              );
              return;
            }
          }

          if (!xMinVazio && !xMaxVazio && Number(xFaixa.min) > Number(xFaixa.max)) {
            props.onMessageError?.(`A faixa ${xIndex + 1} está com mínimo maior que máximo.`);
            return;
          }

          if (xIndex > 0 && !xMinVazio) {
            const xFaixaAnterior = xFaixas[xIndex - 1];
            const xMaxAnteriorVazio =
              xFaixaAnterior.max === '' ||
              xFaixaAnterior.max === null ||
              xFaixaAnterior.max === undefined;

            if (!xMaxAnteriorVazio && Number(xFaixa.min) < Number(xFaixaAnterior.max)) {
              props.onMessageError?.(
                'O mínimo de um item não pode ser menor que o máximo do item anterior.'
              );
              return;
            }
          }
        }
      }

      // Converte o formulário para o domínio de resposta esperado pelo backend.
      const xRespostaDominio =
        isQuestaoInterna || pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
          ? pValues.opcoesMultiplaEscolha.map((xOpcao, xIndex) => ({
              id: xIndex + 1,
              label: isQuestaoInterna ? `Opção ${xIndex + 1}` : (xOpcao.pergunta || '').trim(),
              tooltip: '',
              objetivo: (xOpcao.discriminacao || '').trim(),
              nota: Number(xOpcao.points)
            }))
          : pValues.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
          ? mapOpcoesDicotomicasToRespostaDominio(pValues.opcoesDicotomicas)
          : pValues.faixasNumericas.map((xFaixa, xIndex) => {
              const xMinVazio =
                xFaixa.min === '' || xFaixa.min === null || xFaixa.min === undefined;
              const xMaxVazio =
                xFaixa.max === '' || xFaixa.max === null || xFaixa.max === undefined;

              return {
                id: xIndex + 1,
                label: `${xFaixa.faixa} Opção`,
                tooltip: '',
                objetivo: (xFaixa.discriminacao || '').trim(),
                minimo: xMinVazio ? null : Number(xFaixa.min),
                maximo: xMaxVazio ? null : Number(xFaixa.max),
                valor: xIndex,
                nota: Number(xFaixa.points)
              };
            });

      const xPayload = {
        avaliacao_questao: pValues.questao.trim(),
        avaliacao_questao_tooltip: (pValues.tooltip || '').trim(),
        avaliacao_secao_id: Number(pValues.sessaoId),
        interna: Boolean(pValues.interna),
        resposta_tipo: pValues.tipoResposta,
        resposta_dominio: xRespostaDominio
      };

      services.questoes.add(
        xPayload,
        () => {
          props.onMessageSuccess?.('Questão criada com sucesso.');
          actions.handleChangeActiveSessaoId(pValues.sessaoId);
          navigate('/avaliacoes/configuracoes/questoes');
        },
        rErr => {
          props.onMessageError?.(rErr?.message || 'Não foi possível criar a questão.');
        }
      );
    }
  });

  const { values } = formik;
  const isQuestaoInterna = values.interna;
  const xSessaoSelecionadaValida = sessoesQuestoes.some(
    xSessao => xSessao.sessao_id === Number(values.sessaoId)
  );

  useEffect(() => {
    // Busca as seções reais no serviço e mantém uma seleção válida no formulário.
    services.secoes.list(
      {
        page: 1,
        size: 100,
        sort: 'avaliacao_secao_id asc'
      },
      rRes => {
        const xSessoesFromService = Object.values(rRes?.data || {})
          .map(xSessao => ({
            sessao_id: Number(xSessao?.sessao_id ?? xSessao?.avaliacao_secao_id),
            sessao: xSessao?.sessao ?? xSessao?.avaliacao_secao
          }))
          .filter(xSessao => Number.isFinite(xSessao.sessao_id) && xSessao.sessao);

        if (xSessoesFromService.length) {
          const xSessaoAtualId = Number(formik.values.sessaoId);
          setSessoesQuestoes(xSessoesFromService);
          formik.setFieldValue(
            'sessaoId',
            xSessoesFromService.some(xSessao => xSessao.sessao_id === xSessaoAtualId)
              ? xSessaoAtualId
              : xSessoesFromService[0].sessao_id
          );
        }
      }
    );
  }, []);

  const handleUpdateMultiplaEscolha = (id, field, value) => {
    formik.setFieldValue(
      'opcoesMultiplaEscolha',
      values.opcoesMultiplaEscolha.map(xOpcao => {
        if (xOpcao.id !== id) {
          return xOpcao;
        }
        return { ...xOpcao, [field]: value };
      })
    );
  };

  const handleAddMultiplaEscolha = () => {
    // Limite visual/funcional de opções conforme a regra da tela.
    if (values.opcoesMultiplaEscolha.length >= MAX_DISCRIMINACOES) {
      return;
    }

    formik.setFieldValue('opcoesMultiplaEscolha', [
      ...values.opcoesMultiplaEscolha,
      createMultiplaEscolhaItem(values.opcoesMultiplaEscolha.length)
    ]);
  };

  const handleRemoveMultiplaEscolha = id => {
    if (values.opcoesMultiplaEscolha.length <= 1) {
      return;
    }

    formik.setFieldValue(
      'opcoesMultiplaEscolha',
      values.opcoesMultiplaEscolha
        .filter(xOpcao => xOpcao.id !== id)
        .map((xOpcao, xIndex) => ({
          ...xOpcao,
          opcao: getOpcaoPorIndice(xIndex + 1)
        }))
    );
  };

  const handleUpdateFaixaNumerica = (id, field, value) => {
    formik.setFieldValue(
      'faixasNumericas',
      values.faixasNumericas.map(xFaixa => {
        if (xFaixa.id !== id) {
          return xFaixa;
        }
        return { ...xFaixa, [field]: value };
      })
    );
  };

  const handleAddFaixaNumerica = () => {
    // Limite visual/funcional de faixas conforme a regra da tela.
    if (values.faixasNumericas.length >= MAX_DISCRIMINACOES) {
      return;
    }

    formik.setFieldValue('faixasNumericas', [
      ...values.faixasNumericas,
      createFaixaNumerica(values.faixasNumericas.length)
    ]);
  };

  const handleRemoveFaixaNumerica = id => {
    if (values.faixasNumericas.length <= 1) {
      return;
    }

    formik.setFieldValue(
      'faixasNumericas',
      values.faixasNumericas
        .filter(xFaixa => xFaixa.id !== id)
        .map((xFaixa, xIndex) => ({
          ...xFaixa,
          faixa: `${xIndex + 1}ª`
        }))
    );
  };

  const handleUpdateDicotomica = (id, field, value) => {
    formik.setFieldValue(
      'opcoesDicotomicas',
      values.opcoesDicotomicas.map(xOpcao =>
        xOpcao.id === id ? { ...xOpcao, [field]: value } : xOpcao
      )
    );
  };

  const handleChangeInterna = pChecked => {
    formik.setFieldValue('interna', pChecked);
    formik.setFieldValue(
      'tipoResposta',
      pChecked ? null : values.tipoResposta || AVALIACAO_RESPOSTA_TIPO.MUL.id
    );
  };

  const handleSave = () => formik.submitForm();

  return (
    <BaseTemplate
      title={'Nova Questão'}
      subtitle={'Avaliações'}
      description="Defina o peso de cada questão conforme sua relevância para a avaliação. Os pesos auxiliam na priorização dos critérios e permitem personalizar o impacto das respostas no resultado final."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Divider />
      <Stack direction="row" gap={4} py={3}>
        <Stack component="main" flexGrow={1} pt={3} gap={2}>
          <Stack gap={2}>
            <TextField
              label="Título"
              placeholder="Ex.: Classificação de qualidade de gestão [agências de rating]"
              value={values.questao}
              onChange={formik.handleChange('questao')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={4}
            />
            <TextField
              label="Tooltip"
              placeholder="Informações complementares da questão"
              value={values.tooltip}
              onChange={formik.handleChange('tooltip')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={2}
            />
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Stack gap={2} direction="row">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.interna}
                      onChange={e => handleChangeInterna(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography color="textPrimary" variant="body2">
                      Questão interna
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            <Divider />
            {!isQuestaoInterna && (
              <>
                <Stack gap={1}>
                  <Typography
                    color="textPrimary"
                    variant="caption"
                    sx={{ textTransform: 'uppercase' }}>
                    TIPO DE RESPOSTA
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      disabled={isQuestaoInterna}
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.MUL.id)
                      }>
                      Múltipla escolha
                    </Button>
                    <Button
                      size="small"
                      disabled={isQuestaoInterna}
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.NUM.id)
                      }>
                      Numérica
                    </Button>
                    <Button
                      size="small"
                      disabled={isQuestaoInterna}
                      variant={
                        values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        formik.setFieldValue('tipoResposta', AVALIACAO_RESPOSTA_TIPO.DIC.id)
                      }>
                      Dicotômica
                    </Button>
                  </Stack>
                </Stack>

                <Divider />
              </>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography color="textPrimary" variant="h6">
                {values.interna && values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id
                  ? 'Faixas numéricas'
                  : values.interna && values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id
                  ? 'Opções dicotômicas'
                  : isQuestaoInterna
                  ? 'Avaliação interna'
                  : 'Avaliação'}
              </Typography>
            </Stack>

            {(isQuestaoInterna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id) &&
              values.opcoesMultiplaEscolha.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{OPCOES_DISCRIMINACAO[xIndex]} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={values.opcoesMultiplaEscolha.length === 1}
                      onClick={() => handleRemoveMultiplaEscolha(xOpcao.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label={isQuestaoInterna ? 'Critério interno' : 'Discriminação'}
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateMultiplaEscolha(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                    {!values.interna && (
                      <TextField
                        label="Resposta"
                        placeholder="Texto exibido para resposta"
                        value={xOpcao.pergunta}
                        onChange={pEvent =>
                          handleUpdateMultiplaEscolha(xOpcao.id, 'pergunta', pEvent.target.value)
                        }
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateMultiplaEscolha(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.opcoesMultiplaEscolha.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {!values.interna &&
              values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.DIC.id &&
              values.opcoesDicotomicas.map((xOpcao, xIndex) => (
                <Stack
                  key={xOpcao.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ minHeight: '31px' }}>
                    <Typography variant="body2" color="textSecondary">
                      <b>Opção {xOpcao.opcao}</b>
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <TextField
                      label="Discriminação"
                      placeholder="Critério interno"
                      value={xOpcao.discriminacao}
                      onChange={pEvent =>
                        handleUpdateDicotomica(xOpcao.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xOpcao.points}
                      onChange={pValue => handleUpdateDicotomica(xOpcao.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.opcoesDicotomicas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {!values.interna &&
              values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.NUM.id &&
              values.faixasNumericas.map((xFaixa, xIndex) => (
                <Stack
                  key={xFaixa.id}
                  gap={1.5}
                  sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      <b>{xFaixa.faixa} Opção</b>
                    </Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={values.faixasNumericas.length === 1}
                      onClick={() => handleRemoveFaixaNumerica(xFaixa.id)}>
                      <Icon iconName="delete" color="inherit" size={21} />
                    </IconButton>
                  </Stack>
                  <Divider />
                  <Stack gap={3}>
                    <Stack gap={1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <TextField
                          label="Mínimo"
                          type="number"
                          size="small"
                          value={xFaixa.min}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'min', pEvent.target.value)
                          }
                          fullWidth
                          variant="outlined"
                        />
                        <TextField
                          label="Máximo"
                          type="number"
                          size="small"
                          value={xFaixa.max}
                          onChange={pEvent =>
                            handleUpdateFaixaNumerica(xFaixa.id, 'max', pEvent.target.value)
                          }
                          fullWidth
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>

                    <TextField
                      label="Discriminação"
                      placeholder="Critério interno"
                      value={xFaixa.discriminacao}
                      onChange={pEvent =>
                        handleUpdateFaixaNumerica(xFaixa.id, 'discriminacao', pEvent.target.value)
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack>
                    <Range
                      value={xFaixa.points}
                      onChange={pValue => handleUpdateFaixaNumerica(xFaixa.id, 'points', pValue)}
                    />
                    <QuestaoRating
                      readOnly
                      rating={xIndex}
                      max={xIndex === 0 ? 1 : values.faixasNumericas.length - 1}
                    />
                  </Stack>
                </Stack>
              ))}

            {values.tipoResposta !== AVALIACAO_RESPOSTA_TIPO.DIC.id && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon iconName="insert" size={16} />}
                disabled={
                  values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                    ? values.opcoesMultiplaEscolha.length >= MAX_DISCRIMINACOES
                    : values.faixasNumericas.length >= MAX_DISCRIMINACOES
                }
                onClick={
                  values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                    ? handleAddMultiplaEscolha
                    : handleAddFaixaNumerica
                }>
                {values.interna || values.tipoResposta === AVALIACAO_RESPOSTA_TIPO.MUL.id
                  ? 'Adicionar opção'
                  : 'Adicionar faixa'}
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack
          component="sidebar"
          minWidth={{ lg: 320 }}
          maxWidth={{ lg: 420 }}
          gap={2}
          width="100%"
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack gap={2} px={2} py={3}>
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  SEÇÃO
                </Typography>
                <Select
                  label="Sessão"
                  value={xSessaoSelecionadaValida ? values.sessaoId : ''}
                  onChange={pEvent => formik.setFieldValue('sessaoId', Number(pEvent.target.value))}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ 'aria-label': 'Sessão da questão' }}>
                  {sessoesQuestoes.map(xSessao => (
                    <MenuItem key={xSessao.sessao_id} value={xSessao.sessao_id}>
                      {xSessao.sessao}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              {/* <Divider />
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  MODELOS
                </Typography>
                <Stack flexWrap="wrap" gap={0.5}>
                  <FormGroup>
                    {modelosDisponiveis.map(xModelo => (
                      <FormControlLabel
                        key={xModelo.id}
                        control={<Checkbox checked={false} size="small" />}
                        label={
                          <Typography color="textPrimary" variant="body2">
                            {xModelo.label}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Stack>
              </Stack> */}
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsQuestoesAdd.displayName = 'AvaliacoesSettingsQuestoesAdd';

export default withMessage(AvaliacoesSettingsQuestoesAdd);
